import React, { useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  Chip,
  Card,
  CardContent,
  CardActions,
  Button,
  IconButton,
  Tooltip,
  Grid,
  Divider,
  useTheme,
} from "@mui/material";
import {
  Business,
  Person,
  Email,
  Phone,
  LocationOn,
  Edit,
  ArrowForwardIos,
  PinDrop,
} from "@mui/icons-material";
import InvoiceCreateModal from "../invoices/InvoiceCreateModal";

const CustomerCard = ({ customer, selected, onSelect, onEdit, theme }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);

  // Format contact name - prioritize contactName over email
  const displayName = customer.contactName || customer.email;

  // Handle create invoice button click
  const handleCreateInvoice = () => {
    setShowInvoiceModal(true);
  };

  // Handle modal close
  const handleCloseModal = () => {
    setShowInvoiceModal(false);
  };

  // Extract initials for avatar
  const initials =
    displayName
      ?.split(" ")
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase() || "U";

  return (
    <Card
      sx={{
        mb: 2,
        borderRadius: 3,
        border: `2px solid ${selected ? "#24e4ff" : theme.palette.divider}`,
        backgroundColor: selected
          ? "rgba(36, 228, 255, 0.05)"
          : "background.paper",
        transition: "all 0.2s ease-in-out",
        cursor: "pointer",
        position: "relative",
        "&:hover": {
          borderColor: isHovered ? "#24e4ff" : theme.palette.primary.main,
          transform: "translateY(-2px)",
          boxShadow: theme.shadows[4],
        },
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onSelect(customer.id)}
    >
      <CardContent>
        {/* Header with Avatar */}
        <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
          <Avatar
            sx={{
              width: 64,
              height: 64,
              bgcolor: selected ? "#24e4ff" : theme.palette.primary.main,
              fontSize: "1.5rem",
              fontWeight: "bold",
              color: selected ? "#000" : "#fff",
              mr: 2,
              flexShrink: 0,
            }}
          >
            {initials}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Company Name - Primary Heading */}
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                marginBottom: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              <Business
                sx={{ fontSize: 20, verticalAlign: "middle", mr: 0.5 }}
              />
              {customer.companyName || "No Company Name"}
            </Typography>

            {/* Contact Name - Secondary Heading */}
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontWeight: 500,
                mb: 1,
              }}
            >
              <Person sx={{ fontSize: 16, verticalAlign: "middle", mr: 0.5 }} />
              {displayName || "No Contact Name"}
            </Typography>

            {/* Status Chip */}
            <Chip
              label={customer.subscriptionActive ? "Active" : "Inactive"}
              color={customer.subscriptionActive ? "success" : "error"}
              size="small"
              sx={{ fontWeight: 600 }}
            />
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Contact Details Grid */}
        <Grid container spacing={2}>
          {/* Email */}
          {customer.email && (
            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Email
                  sx={{
                    fontSize: 18,
                    color: theme.palette.text.secondary,
                    mr: 1,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
                >
                  {customer.email}
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Phone */}
          {customer.phoneNumber && (
            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Phone
                  sx={{
                    fontSize: 18,
                    color: theme.palette.text.secondary,
                    mr: 1,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
                >
                  {customer.phoneNumber}
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Address */}
          {customer.address && (
            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <LocationOn
                  sx={{
                    fontSize: 18,
                    color: theme.palette.text.secondary,
                    mr: 1,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
                >
                  {customer.address}
                </Typography>
              </Box>
            </Grid>
          )}

          {/* City/State */}
          {(customer.city || customer.state) && (
            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <PinDrop
                  sx={{
                    fontSize: 18,
                    color: theme.palette.text.secondary,
                    mr: 1,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
                >
                  {customer.city}
                  {customer.city && customer.state && ", "}
                  {customer.state}
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </CardContent>

      <CardActions sx={{ justifyContent: "space-between", px: 2, pb: 2 }}>
        <Tooltip title="Create Invoice">
          <Button
            variant="contained"
            size="large"
            onClick={handleCreateInvoice}
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              px: 3,
              py: 1,
              bgcolor: selected ? "#24e4ff" : theme.palette.primary.main,
              color: "#000",
              "&:hover": {
                bgcolor: "#35d33d",
              },
            }}
          >
            Create Invoice
            <ArrowForwardIos sx={{ ml: 1, fontSize: 18 }} />
          </Button>
        </Tooltip>

        <Tooltip title="Edit User">
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              onEdit(customer);
            }}
            sx={{
              color: theme.palette.text.secondary,
              "&:hover": {
                color: theme.palette.primary.main,
              },
            }}
          >
            <Edit />
          </IconButton>
        </Tooltip>
      </CardActions>

      {/* Invoice Create Modal */}
      <InvoiceCreateModal
        open={showInvoiceModal}
        onClose={handleCloseModal}
        customerData={customer}
        customerName={customer.companyName || displayName}
      />
    </Card>
  );
};

export default CustomerCard;
